import { LANDING_FEATURE_LIMIT } from "./landingPlanTypes";

/**
 * The four lengths that have a period name. Labels are lowercase.
 * Any other positive length stays a day count — never "mensal".
 */
const PERIOD_LABEL_BY_DAYS = new Map<number, string>([
  [30, "mensal"],
  [90, "trimestral"],
  [180, "semestral"],
  [365, "anual"],
]);

/**
 * Period copy for a raw day count.
 *
 * Names only 30, 90, 180, and 365. Any other positive finite number is
 * "Acesso por N dias": an integer has no decimal point, and a fractional
 * count is `String(amountOfDays)`. Refuses to invent "mensal" when the
 * length is missing, not a number (including the string "30"), NaN,
 * Infinity, zero, or negative.
 */
export function periodLabel(amountOfDays: unknown): string {
  if (typeof amountOfDays !== "number" || !Number.isFinite(amountOfDays) || amountOfDays <= 0) {
    return "";
  }

  const known = PERIOD_LABEL_BY_DAYS.get(amountOfDays);
  if (known !== undefined) {
    return known;
  }

  const days = Number.isInteger(amountOfDays)
    ? String(Math.trunc(amountOfDays))
    : String(amountOfDays);
  return `Acesso por ${days} dias`;
}

/**
 * Product description for the landing.
 *
 * Returns a string unchanged, including a string of spaces. Refuses to
 * invent copy or fill from a static catalog: `undefined`, `null`, and any
 * non-string become "".
 */
export function landingDescription(description: unknown): string {
  if (typeof description !== "string") {
    return "";
  }
  return description;
}

/**
 * Feature lines a landing card may list, and how many strings were left off.
 *
 * Keeps only strings, in their original order, including empty strings.
 * Lists the first `LANDING_FEATURE_LIMIT` and counts the rest. Refuses to
 * invent, sort, or trim features. A value that is not an array lists none.
 */
export function landingFeatures(features: unknown): { features: string[]; featureRemainder: number } {
  if (!Array.isArray(features)) {
    return { features: [], featureRemainder: 0 };
  }

  const lines = features.filter((item): item is string => typeof item === "string");
  const listed = lines.slice(0, LANDING_FEATURE_LIMIT);
  return {
    features: listed,
    featureRemainder: lines.length - listed.length,
  };
}

/**
 * Product title for the landing.
 *
 * Returns `title` unchanged. Refuses to append a cycle, edition, year,
 * periodicity, or "pré-edital". A title that contains "2027" comes back
 * identical, and this function adds no second label.
 */
export function landingTitle(title: string): string {
  return title;
}
